import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, MessageSquareX, Flame, Shuffle, UserX,
  Users, Brain, Mic, Flower2, ShieldCheck,
  Briefcase, Target, Award, BadgeCheck,
  Phone, ClipboardList, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import AuthorByline from "@/components/AuthorByline";
import RelatedServices from "@/components/RelatedServices";
import { useSitePage } from "@/lib/cms";
import { getRecordArray, getString, getStringArray } from "@/lib/pageContent";
import { buildBreadcrumb, buildService, buildFAQ, CORE_FAQS } from "@/lib/seo/schemas";

const problemIcons = [MessageSquareX, Flame, Shuffle, UserX];
const partnerIcons = [Briefcase, Target, Award, BadgeCheck];
const journeyIcons = [Phone, ClipboardList, Sparkles];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const ServicesCompanies = () => {
  const { data: page } = useSitePage("services-companies");
  const content = (page?.content ?? {}) as Record<string, unknown>;

  const problems = getRecordArray<Record<string, unknown>>(content.problems, []).map((item, index) => ({
    title: getString(item.title, `Problem ${index + 1}`),
    desc: getString(item.desc),
  }));
  const whyPartner = getRecordArray<Record<string, unknown>>(content.whyPartner, []).map((item, index) => ({
    title: getString(item.title, `Reason ${index + 1}`),
    desc: getString(item.desc),
  }));

  return (
    <div>
      <SEOHead
        title={page?.seo.title ?? "Corporate Training Dubai — EI, Resilience & Wellness"}
        description={page?.seo.description ?? "Emotional intelligence, resilience, burnout prevention and stress management training for teams in Dubai. Practical corporate wellness that lifts performance."}
        canonical={page?.seo.canonical}
        ogTitle={page?.seo.ogTitle}
        ogDescription={page?.seo.ogDescription}
        ogImage={page?.seo.ogImage}
        type={page?.seo.type ?? "website"}
        schema={page?.seo.schema ?? [
          buildService({
            name: "Corporate Leadership & Soft Skills Training",
            description: "MVIBE facilitates leadership offsites, executive presence, communication and POSH training for corporates across Dubai, UAE and India.",
            path: "/services/companies",
            serviceType: "Corporate training",
          }),
          buildBreadcrumb([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
            { name: "For Organisations", path: "/services/companies" },
          ]),
          buildFAQ(CORE_FAQS.slice(0, 6)),
        ]}
      />

      {/* Hero */}
      <section className="bg-primary py-14 md:py-20">
        <div className="container mx-auto px-4 text-center">
          <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
            <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <Link to="/services" className="transition-colors hover:text-primary-foreground">Services</Link>
            <ChevronRight className="h-4 w-4" />
            <span className="text-primary-foreground">For Organisations</span>
          </nav>
          <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold text-primary-foreground md:text-5xl lg:text-6xl">
            {getString(content.heroTitle, "Corporate Training & Consulting: Driving Success Beyond.")}
          </motion.h1>
          <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-2xl font-sans text-lg text-primary-foreground/80">
            {getString(content.heroDescription, "Tailored soft skills training and strategic consulting for today's dynamic workplace.")}
          </motion.p>
        </div>
      </section>

      {/* Challenges */}
      <section className="bg-background py-12">
        <div className="container mx-auto px-4">
          <motion.h2 {...fadeUp()} className="text-center text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.problemsTitle, "Challenges Your Organisation May Face")}</motion.h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {problems.map((problem, i) => {
              const Icon = problemIcons[i] ?? MessageSquareX;
              return (
                <motion.div key={problem.title} {...fadeUp(i * 0.08)} className="rounded-xl bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15">
                    <Icon className="h-5 w-5 text-accent" />
                  </div>
                  <h3 className="mt-3 text-lg font-heading font-bold text-foreground">{problem.title}</h3>
                  <p className="mt-1.5 font-sans text-base leading-relaxed text-muted-foreground">{problem.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Executive Facilitation & Soft Skills */}
      <section id="executive-facilitation" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600"
            alt="Executive facilitation and soft skills training"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">{getString(content.corporateFocusTitle, "Soft Skills & Behavioural Training for Corporates")}</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">CXOs, DGMs, Senior Leadership Teams</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Business-contextual, discussion-driven, case-based learning and peer dialogue. Delivered face-to-face in 3–4 hour modules or leadership offsites.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Executive presence & leadership communication", "Stakeholder management & influence", "Strategic thinking & decision making", "Cross-functional collaboration", "Change management & resilience"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Users className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-4 border-l-4 border-accent py-1.5 pl-3">
              <p className="font-sans text-base italic leading-relaxed text-muted-foreground">{getString(content.corporateNote, "Business-contextual, discussion-driven, case-based learning, peer dialogue.")}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Communication & Impact */}
      <section id="communication" className="bg-background py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Communication, Impact & Personal Branding</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">For teams and emerging leaders</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Empower your workforce with impactful communication skills, presentation mastery, and the confidence to represent your brand with authenticity and conviction.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Professional communication & email etiquette", "Presentation skills & public speaking", "Personal branding for corporate professionals", "Confident body language & executive image"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Mic className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"
            alt="Communication and personal branding training"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
        </div>
      </section>

      {/* Creative Resilience & Art Therapy */}
      <section id="art-therapy" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=600"
            alt="Art Therapy workshop for organisations"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Creative Resilience & Stress Management</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">Team wellness, creativity & stress management</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Art Therapy workshops help teams manage workplace stress, boost creativity, and strengthen interpersonal connections through guided creative exercises. No artistic experience needed — it's about the process, not the product.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Team stress management & burnout prevention", "Creative problem-solving workshops", "Emotional wellness and team bonding", "Mindfulness through art-based exercises"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Flower2 className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* POSH & Workplace Wellness */}
      <section id="posh-wellness" className="bg-background py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">POSH & Workplace Wellness</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">Certified POSH training & holistic employee wellness</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Mahirah is a Certified POSH Trainer delivering mandatory Prevention of Sexual Harassment training alongside broader workplace wellness programmes. Build a safer, healthier, and more productive work environment.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["POSH compliance training (mandatory by law)", "Creating safe & inclusive workplaces", "Employee mental health awareness", "Building respectful workplace culture"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <ShieldCheck className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
          <motion.img
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600"
            alt="POSH and workplace wellness training"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
        </div>
      </section>

      {/* Behavioural Insight */}
      <section id="behavioural-insight" className="bg-muted py-12">
        <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
          <motion.img
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            src="https://images.unsplash.com/photo-1455390582262-044cdead277a?w=600"
            alt="Behavioural insight and self-awareness"
            className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
            loading="lazy"
          />
          <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
            <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Behavioural Insight & Self-Awareness Labs</h2>
            <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">Graphology, EI & personality insights for teams</p>
            <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
              Unique workshops combining handwriting analysis, emotional intelligence assessments, and behavioural profiling to build deeper self-awareness and team dynamics understanding.
            </p>
            <ul className="mt-4 space-y-2.5">
              {["Personality profiling & team dynamics", "Emotional intelligence development", "Graphology-based self-awareness", "Interpersonal skills enhancement"].map((item) => (
                <li key={item} className="flex items-center gap-2.5">
                  <Brain className="h-5 w-5 shrink-0 text-accent" />
                  <span className="font-sans text-base text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Why Partner */}
      <section className="bg-background py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-foreground md:text-5xl">{getString(content.whyPartnerTitle, "Why Partner with MVIBE?")}</motion.h2>
          <div className="mt-8 grid gap-6 md:grid-cols-3 lg:grid-cols-4">
            {whyPartner.map((item, i) => {
              const Icon = partnerIcons[i] ?? Briefcase;
              return (
                <motion.div key={item.title} {...fadeUp(i * 0.12)} className="relative rounded-xl bg-card p-6 text-center shadow-sm">
                  <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-xl font-bold text-accent-foreground">{i + 1}</div>
                  <Icon className="mx-auto mt-2.5 h-5 w-5 text-accent" />
                  <h3 className="mt-2.5 text-lg font-heading font-bold text-foreground">{item.title}</h3>
                  <p className="mt-1.5 font-sans text-base text-muted-foreground">{item.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-accent py-12">
        <div className="container mx-auto px-4 text-center">
          <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-accent-foreground md:text-5xl">{getString(content.ctaTitle, "Let's Discuss a Tailored Training Solution")}</motion.h2>
          <motion.div {...fadeUp(0.15)} className="mt-6">
            <DiscoveryCallDialog>
              <Button size="lg" className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
                {getString(content.ctaButtonLabel, "Get Started →")}
              </Button>
            </DiscoveryCallDialog>
          </motion.div>
        </div>
      </section>

      <section className="bg-background py-10">
        <div className="container mx-auto px-4">
          <AuthorByline />
        </div>
      </section>

      <RelatedServices current="companies" />
    </div>
  );
};

export default ServicesCompanies;